<template>
  <Transition>
    <TopBanner v-if="bannerText" :mode="bannerMode" :text="bannerText" />
  </Transition>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div>
          <h1 class="admin-page-title">Löhne</h1>
          <p class="admin-page-subtitle">Lohnabrechnungen importieren und DATEV-Exporte erstellen.</p>
        </div>
      </div>

      <section class="admin-panel">
        <div class="admin-panel__header">
          <h2 class="admin-panel__title">Zusammengefasste Lohnabrechnungen importieren</h2>
          <p class="admin-page-subtitle">Das PDF wird anhand des Blatt-Zählers getrennt und über die Pers.-Nr. dem Mitarbeiter zugeordnet.</p>
        </div>
        <div class="admin-panel__body">
          <div class="grid gap-4 md:grid-cols-2">
            <label class="block">
              <span class="mb-2 block text-sm font-bold text-gray-700">Abrechnungsmonat</span>
              <input v-model="importMonth" type="month" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5" />
            </label>
            <label class="block">
              <span class="mb-2 block text-sm font-bold text-gray-700">PDF-Datei</span>
              <input ref="fileInput" type="file" accept="application/pdf,.pdf" class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm" @change="selectFile" />
            </label>
          </div>
          <div v-if="selectedFile" class="mt-3 text-sm text-gray-600">{{ selectedFile.name }} ({{ formatBytes(selectedFile.size) }})</div>
          <button type="button" class="admin-button--primary mt-5" :disabled="!selectedFile || loading" @click="previewDocument">
            {{ loading ? 'PDF wird analysiert...' : 'Abrechnungen prüfen' }}
          </button>
        </div>
      </section>

      <section v-if="documents.length" class="admin-panel mt-6">
        <div class="admin-panel__header flex flex-wrap items-center justify-between gap-2">
          <h2 class="admin-panel__title">Vorschau: {{ documents.length }} Abrechnung(en) gefunden</h2>
          <span class="text-sm text-gray-500">Monat: {{ importMonth }}</span>
        </div>
        <div class="admin-panel__body">
          <div class="overflow-x-auto">
            <table class="m-0 w-full min-w-[620px] border-collapse text-sm shadow-none">
              <thead class="bg-gray-700 text-left text-white">
                <tr><th class="p-3">Blätter</th><th class="p-3">Pers.-Nr.</th><th class="p-3">Mitarbeiter</th><th class="p-3">Status</th></tr>
              </thead>
              <tbody>
                <tr v-for="document in documents" :key="document.index" class="border-b">
                  <td class="p-3">{{ document.pageCount }} ({{ document.pages ? document.pages.join(', ') : '-' }})</td>
                  <td class="p-3 font-bold">{{ document.persnr || '-' }}</td>
                  <td class="p-3">
                    <button v-if="document.employee" type="button" class="admin-button--link" @click="openEmployee(document.employee)">{{ employeeName(document.employee) }}</button>
                    <span v-else class="text-red-600">Nicht gefunden</span>
                  </td>
                  <td class="p-3" :class="document.employee ? 'text-green-700' : 'text-red-600'">{{ document.employee ? 'Zugeordnet' : 'Keine Zuordnung' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-if="hasUnmapped" class="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">Bitte alle Pers.-Nr. prüfen. Nicht zugeordnete Abrechnungen können nicht importiert werden.</p>
          <button type="button" class="admin-button--primary mt-5" :disabled="hasUnmapped || loading" @click="confirmImport">{{ loading ? 'Import läuft...' : 'Abrechnungen bestätigen und speichern' }}</button>
        </div>
      </section>

      <section class="admin-panel mt-6">
        <div class="admin-panel__header">
          <h2 class="admin-panel__title">DATEV-Export</h2>
          <p class="admin-page-subtitle">Erstellt die DATEV-Liste für den ausgewählten Abrechnungsmonat.</p>
        </div>
        <div class="admin-panel__body">
          <label class="block max-w-sm" for="datev-export-month">
            <span class="mb-2 block text-sm font-bold text-gray-700">Abrechnungsmonat</span>
            <input id="datev-export-month" v-model="exportMonth" type="month" class="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5" />
          </label>
          <button type="button" class="admin-button--primary mt-5" :disabled="datevExporting" @click="exportDatevList">{{ datevExporting ? 'Export wird erstellt...' : 'DATEV-Liste erstellen' }}</button>
        </div>
      </section>
    </div>
  </div>
  <UserProfileModal v-if="selectedUser" :user="selectedUser" @close="selectedUser = null" />
</template>

<script>
import CASP from '../../CASP'
import { useCookies } from 'vue3-cookies'
import TopBanner from '@/components/TopBanner'
import UserProfileModal from './UserProfileModal.vue'

export default {
  name: 'AdminLohn',
  components: { TopBanner, UserProfileModal },
  setup () {
    const { cookies } = useCookies()
    return { cookies }
  },
  data () {
    return {
      importMonth: new Date().toISOString().slice(0, 7),
      exportMonth: new Date().toISOString().slice(0, 7),
      selectedFile: null,
      documents: [],
      loading: false,
      datevExporting: false,
      selectedUser: null,
      bannerText: '',
      bannerMode: 'success'
    }
  },
  computed: {
    hasUnmapped () {
      return this.documents.some(document => !document.employee)
    }
  },
  methods: {
    selectFile (event) {
      this.selectedFile = event.target.files[0] || null
      this.documents = []
    },
    previewDocument () {
      this.upload(true)
    },
    confirmImport () {
      this.upload(false)
    },
    upload (preview) {
      if (!this.selectedFile || this.loading) return
      this.loading = true
      CASP.uploadLohnabrechnungen(this.selectedFile, this.importMonth, preview, (status, response) => {
        this.loading = false
        if (status === 200 && response) {
          if (preview) this.documents = response.documents || []
          if (!preview && response.created) {
            this.showBanner(`${response.created.length} Abrechnung(en) gespeichert.`)
            this.documents = []
            this.selectedFile = null
            this.$refs.fileInput.value = ''
          }
        } else {
          this.showBanner(response && response.desc ? response.desc : 'Das PDF konnte nicht verarbeitet werden.', 'error')
        }
      })
    },
    openEmployee (employee) {
      this.selectedUser = employee
    },
    exportDatevList () {
      if (this.datevExporting) return
      this.datevExporting = true
      CASP.exportDatevForAll(this.exportMonth, (status, response) => {
        this.datevExporting = false
        if (status === 200 && response && response.lodasOtl && response.readableOtl) {
          const apiUrl = process.env.VUE_APP_API_URL
          window.open(`${apiUrl}/assets/${response.lodasOtl}`, '_blank')
          window.open(`${apiUrl}/assets/${response.readableOtl}`, '_blank')
          this.showBanner('DATEV-Liste erstellt.')
        } else {
          this.showBanner(response && response.desc ? response.desc : 'Fehler beim Erstellen der DATEV-Liste.', 'error')
        }
      })
    },
    employeeName (employee) {
      return `${employee.name || ''} ${employee.surname || ''}`.trim() || employee.persnr
    },
    formatBytes (bytes) {
      if (!bytes) return '0 Bytes'
      const units = ['Bytes', 'KB', 'MB', 'GB']
      const index = Math.floor(Math.log(bytes) / Math.log(1024))
      return `${(bytes / Math.pow(1024, index)).toFixed(index ? 1 : 0)} ${units[index]}`
    },
    showBanner (text, mode = 'success') {
      this.bannerText = text
      this.bannerMode = mode
      setTimeout(() => { this.bannerText = '' }, 4000)
    }
  },
  created () {
    const token = this.cookies.get('caspauth') || this.cookies.get('authtoken')
    if (!token) window.location = '/admin/login'
  }
}
</script>
